import React from 'react';
import { X, Link2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { SlicerControl } from '@/components/report-builder/SlicerControl';
import { getSlicerSetting, sortSlicerValues } from '@/lib/slicerSettings';
import { resolveLabel } from '@/lib/fieldLabel';
import { FilterFieldPicker } from '@/components/report-builder/FilterFieldPicker';
import { currentFiscalPeriod } from '@/lib/fiscalLabels';
import { isDateField } from '@/lib/dateSlicerUtils';

/**
 * Global filter bar — drop-zone for dimensions plus position picker (top/left/right/bottom).
 * Renders as a horizontal strip when position is 'top' | 'bottom' and a vertical sidebar
 * when 'left' | 'right'. The cross-filter pill (when active) sits at the trailing edge.
 *
 * Used inside ReportBuilderPage's main canvas area; kept in sync with the global filter
 * state (`globalFilterDims`, `globalFilterValues`) plus the cross-filter that can come
 * from any visual click.
 */
export function GlobalFilterBar({
  position, onPositionChange,
  globalFilterDims, setGlobalFilterDims,
  globalFilterValues, setGlobalFilterValues,
  globalSlicerValues,
  slicerDisplayModes, setSlicerDisplayModes,
  crossFilter, onClearCrossFilter,
  dateFields = [],
  defaultDateField = null,
  fiscalStartMonth = 1,
  allFields = [],
  cascadeSlicers = true,
  onToggleCascade,
  actions = null,
  slicerSettings = {},
  onOpenSettings = null,
  onFieldProperties = null,
  fieldOverrides = {},
}) {
  const isVertical = position === 'left' || position === 'right';
  const isRight = position === 'right';
  const isFiscal = (Number(fiscalStartMonth) || 1) !== 1;
  const asOf = globalFilterValues['__asof__'];
  // For a non-January fiscal model, speak the to-date presets in fiscal terms.
  const presetLabel = (preset, calendarLabel) => {
    if (!isFiscal) return calendarLabel;
    const fp = currentFiscalPeriod(preset, fiscalStartMonth, asOf);
    const fiscalNames = {
      this_month_to_date: 'This fiscal month to date',
      this_quarter_to_date: 'This fiscal quarter to date',
      this_year_to_date: 'This fiscal year to date',
    };
    const base = fiscalNames[preset] || calendarLabel;
    return fp ? `${base} (${fp})` : base;
  };
  const activePreset = (globalFilterValues['__daterange__'] || {}).preset || '';
  const activeFiscal = isFiscal ? currentFiscalPeriod(activePreset, fiscalStartMonth, asOf) : null;

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-primary/50');
    try {
      const d = JSON.parse(e.dataTransfer.getData('application/json'));
      if (d.type === 'dimension' && !globalFilterDims.includes(d.name)) {
        setGlobalFilterDims(prev => [...prev, d.name]);
      }
    } catch { /* drag data wasn't JSON — ignore */ }
  };

  const removeDim = (dim) => {
    setGlobalFilterDims(prev => prev.filter(d => d !== dim));
    setGlobalFilterValues(prev => { const c = { ...prev }; delete c[dim]; return c; });
  };

  return (
    <div
      className={isVertical
        ? `flex flex-col w-52 ${isRight ? 'border-l' : 'border-r'} border-border/50 p-2 gap-2 overflow-y-auto`
        : 'px-4 py-2 border-b border-border/50 flex items-center gap-2 overflow-x-auto'}
      style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(12px)' }}
      onContextMenu={e => e.stopPropagation()}
      data-testid="global-filter-bar"
    >
      <div className={`flex items-center gap-2 ${isVertical ? 'flex-wrap' : ''}`}>
        <span className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold flex-shrink-0">Filters</span>
        <div className="flex gap-0.5 flex-shrink-0">
          {[['top','T'],['left','L'],['right','R'],['bottom','B']].map(([pos, label]) => (
            <button
              key={pos}
              onClick={() => onPositionChange(pos)}
              className={`w-4 h-4 rounded text-[7px] font-bold transition-colors ${position === pos ? 'bg-primary text-primary-foreground' : 'bg-muted/30 text-muted-foreground hover:bg-muted/50'}`}
              title={`Move filters to ${pos}`}
              data-testid={`global-filter-position-${pos}`}
            >{label}</button>
          ))}
        </div>
        <select
          className="text-[9px] rounded-md border border-border/40 bg-muted/20 px-1.5 py-0.5 text-foreground flex-shrink-0 focus:outline-none focus:ring-1 focus:ring-primary/40"
          value={(globalFilterValues['__daterange__'] || {}).preset || ''}
          onChange={e => {
            const p = e.target.value;
            setGlobalFilterValues(prev => {
              const c = { ...prev };
              if (p) c['__daterange__'] = { preset: p };
              else delete c['__daterange__'];
              return c;
            });
          }}
          title="Relative date range — applies to every visual and stays current in scheduled reports"
          data-testid="global-date-range-preset"
        >
          <option value="">Date range: All time</option>
          <option value="today">Today</option>
          <option value="this_month_to_date">{presetLabel('this_month_to_date', 'This month to date')}</option>
          <option value="this_quarter_to_date">{presetLabel('this_quarter_to_date', 'This quarter to date')}</option>
          <option value="this_year_to_date">{presetLabel('this_year_to_date', 'This year to date')}</option>
          <option value="inception">Inception to date</option>
        </select>
        {activeFiscal && (
          <Badge variant="outline" className="text-[9px] border-indigo-500/40 text-indigo-300 flex-shrink-0" data-testid="global-fiscal-period">
            {activeFiscal}
          </Badge>
        )}
        {(globalFilterValues['__daterange__'] || {}).preset && dateFields.length > 1 && (
          <select
            className="text-[9px] rounded-md border border-border/40 bg-muted/20 px-1.5 py-0.5 text-foreground flex-shrink-0 focus:outline-none focus:ring-1 focus:ring-primary/40"
            value={(globalFilterValues['__daterange__'] || {}).field || ''}
            onChange={e => {
              const fld = e.target.value;
              setGlobalFilterValues(prev => ({ ...prev, __daterange__: { ...(prev['__daterange__'] || {}), field: fld || undefined } }));
            }}
            title="Which date field the range applies to"
            data-testid="global-date-range-field"
          >
            <option value="">{defaultDateField ? `On: ${defaultDateField.replace(/_/g, ' ')} (default)` : 'On: primary date'}</option>
            {dateFields.filter(f => f !== defaultDateField).map(f => (
              <option key={f} value={f}>On: {f.replace(/_/g, ' ')}</option>
            ))}
          </select>
        )}
        <label className="flex items-center gap-1 text-[9px] text-muted-foreground flex-shrink-0" title="'As of' date for YTD/QTD/MTD/ITD to-date measures — applies to every to-date value and AI answer in this report">
          As of
          <input
            type="date"
            className="text-[9px] rounded-md border border-border/40 bg-muted/20 px-1 py-0.5 text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
            value={globalFilterValues['__asof__'] || ''}
            onChange={e => {
              const v = e.target.value;
              setGlobalFilterValues(prev => {
                const c = { ...prev };
                if (v) c['__asof__'] = v; else delete c['__asof__'];
                return c;
              });
            }}
            data-testid="global-as-of-date"
          />
          {globalFilterValues['__asof__'] && (
            <button
              onClick={() => setGlobalFilterValues(prev => { const c = { ...prev }; delete c['__asof__']; return c; })}
              className="text-muted-foreground/70 hover:text-destructive"
              title="Reset to today"
              data-testid="global-as-of-clear"
            ><X className="w-2.5 h-2.5" /></button>
          )}
        </label>
      </div>
      {onToggleCascade && (
        <button
          onClick={() => onToggleCascade(!cascadeSlicers)}
          className={`flex items-center gap-1 text-[9px] px-2 py-1 rounded-lg border flex-shrink-0 transition-colors ${cascadeSlicers ? 'border-primary/40 bg-primary/10 text-primary' : 'border-border/40 bg-muted/20 text-muted-foreground'}`}
          title={cascadeSlicers ? 'Slicers narrow each other. Click to make them independent.' : 'Slicers are independent. Click to let them narrow each other.'}
          data-testid="cascade-slicers-toggle"
        >
          <Link2 className="w-3 h-3" />
          Cascade {cascadeSlicers ? 'on' : 'off'}
        </button>
      )}
      <div
        className={`rounded-lg border-2 border-dashed border-border/30 min-h-[28px] px-2 py-0.5 flex ${isVertical ? 'flex-col' : 'flex-nowrap overflow-x-auto'} items-center gap-1 flex-1`}
        onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-primary/50'); }}
        onDragLeave={e => e.currentTarget.classList.remove('border-primary/50')}
        onDrop={handleDrop}
        data-testid="global-filter-shelf"
      >
        {globalFilterDims.map(dim => (
          <div key={dim} className="flex items-center gap-1 flex-shrink-0">
            <SlicerControl
              dimension={dim}
              label={resolveLabel(allFields, dim, fieldOverrides)}
              values={sortSlicerValues(globalSlicerValues[dim] || [], getSlicerSetting(slicerSettings, dim).sortOrder)}
              selected={globalFilterValues[dim]}
              onChange={val => setGlobalFilterValues(p => ({ ...p, [dim]: val }))}
              displayMode={slicerDisplayModes[dim]}
              onDisplayModeChange={(d, m) => setSlicerDisplayModes(prev => ({ ...prev, [d]: m }))}
              isDate={(dateFields || []).includes(dim) || isDateField((allFields || []).find(f => f.name === dim))}
              fiscalStartMonth={fiscalStartMonth}
              settings={getSlicerSetting(slicerSettings, dim)}
              onOpenSettings={onOpenSettings}
              onFieldProperties={onFieldProperties}
            />
            <button
              onClick={() => removeDim(dim)}
              className="text-muted-foreground/70 hover:text-destructive flex-shrink-0"
              data-testid={`global-filter-remove-${dim}`}
            ><X className="w-3 h-3" /></button>
          </div>
        ))}
        {globalFilterDims.length === 0 && (
          <span className="text-[9px] text-muted-foreground/30 italic">Drag dimensions here</span>
        )}
        <FilterFieldPicker
          dims={(allFields || []).filter(f => f && f.semantic_type !== 'measure' && !f._sourceField && !globalFilterDims.includes(f.name))}
          placeholder="+ Add filter"
          testid="global-filter-add"
          triggerClassName="h-6 px-2 text-[9px] rounded-md bg-muted/40 border border-border/40 flex items-center gap-1 flex-shrink-0 text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors"
          onPick={(name) => setGlobalFilterDims(prev => prev.includes(name) ? prev : [...prev, name])}
        />
      </div>
      {crossFilter && (
        <Badge className="text-[8px] bg-indigo-500 text-white border-indigo-600 gap-1 flex-shrink-0">
          {crossFilter.field.replace(/_/g, ' ')} = {String(crossFilter.value)}
          <button onClick={onClearCrossFilter} data-testid="clear-cross-filter"><X className="w-2.5 h-2.5" /></button>
        </Badge>
      )}
      {actions && (
        <div className={isVertical ? 'mt-1 pt-2 border-t border-border/40 flex justify-center' : 'ml-auto pl-2 flex-shrink-0'}>
          {actions}
        </div>
      )}
    </div>
  );
}
